import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Skeleton,
    TextField,
    Theme,
    Typography,
} from '@mui/material';
import {
    AccordianMinusIcon,
    AccordianPlusIcon,
    BlackExitIcon,
    BlueTickIcon,
    CheckIcon,
    DialogCloseIcon,
    InvertedDrakDropDownIcon,
    SearchIcon,
    UnCheckIcon
} from '../../../assets/SvgIcons';
import { useState } from 'react';
import {
    CustomFilterProps,
    FilterCheckboxProps,
    TableHeaderData,
} from '../types';
import {
    AmcFilter,
    CategoryFilter,
    FoliosFilter,
    RatingFilter,
    RiskFilter,
    StatusFilter,
} from '../../../redux-store/types/investorProfile';
import { ProductTypeFilter, TransactionTypeFilter, PaymentTypeFilter } from '../../../redux-store/types/orderManagement';
import { useLocation } from 'react-router';

export function FilterCheckbox({
    value,
    label,
    checked,
    handleChange
}: FilterCheckboxProps) {
    return (
        <FormControlLabel
            value={value}
            control={
                <Checkbox
                    size="small"
                    checkedIcon={<CheckIcon />}
                    icon={<UnCheckIcon />}
                    sx={{
                        '&:hover': {
                            backgroundColor: 'transparent',
                        },
                    }}
                    checked={checked}
                    onChange={handleChange}
                    onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            handleChange({ target: { checked: !checked } } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                />
            }
            label={
                <Typography
                    variant="body_500"
                    sx={(theme: Theme) => ({
                        color: theme.palette.text.primary,
                        wordBreak: "break-all"
                    })}>
                    {label}
                </Typography>
            }
            sx={{ display: 'flex' }}
        />
    );
}

export const FilterLoader = () => {
    return (
        <Box
            sx={{
                marginTop: '25px',
                display: "flex",
                flexDirection: "column",
                rowGap: "10px"
            }}
        >
            {[...Array(5)].map((_, index) => (
                <Skeleton
                    key={index}
                    animation="wave"
                    variant="rounded"
                    height={35}
                />
            ))}
        </Box>
    )
};

// Filters in this file =>
// AMC Filter
// Folios Filter
// Status Filter
// Category Filter
// Risk Filter
// Account Type Filter
// Investment Data Filter
// Market Data Filter
// Market Ratios Filter


export default function CustomFilter({
    isLoading = false,
    handleClearAll,
    showAmcFilter = true,
    amcFilterData = [],
    showCategoryFilter = true,
    categoryFilterData = {},
    showRiskFilter = true,
    riskFilterData = [],
    showRatingFilter = true,
    ratingFilterData = [],
    showProductTypeFilter = false,
    showTransactionTypeFilter = false,
    showPaymentTypeFilter = false,
    productTypeFilterData = [],
    transactionFilterData = [],
    paymentFilterData = [],
    handleApplyFilters = () => { return; },
    handleAmcFilterChange = () => { return; },
    handleCategoryFilterChange = () => { return; },
    handleRiskFilterChange = () => { return; },
    handleRatingFilterChange = () => { return; },
    handleProductTypeFilterChange = () => { return; },
    handleTransactionTypeFilterChange = () => { return; },
    handlePaymentTypeFilterChange = () => { return; },
    showCloseIcon = false,
    handleCloseIconClick = () => { return; },
    showAccountTypeFilter = false,
    accountTypeFilterData = [],
    handleAccountTypeFilterChange = () => { return; },
    showStatusFilter = false,
    statusFilterData = [],
    handleStatusFilterChange = () => { return; },
    showInvestmentDataFilter = false,
    investmentFilterData = [],
    handleInvestmentDataFilterChange = () => { return; },
    showMarketDataFilter = false,
    marketFilterData = [],
    handleMarketDataFilterChange = () => { return; },
    showMarketRatiosFilter = false,
    marketRatiosFilterData = [],
    handleMarketRatiosFilterChange = () => { return; },
    showFoliosFilter = false,
    foliosFilterData = [],
    handleFoliosFilterChange = () => { return; },
    showApprovalTypeFilter = false,
    approvalTypeFilterData = [],
    handleApprovalTypeFilterChange = () => { return; },
    isFilterApplied = false,
    defaultExpandedFilter = "",
    showApplyButton = true,
}: CustomFilterProps): JSX.Element {

    const [isEquityFilterExpanded, setEquityFilterExpanded] = useState<boolean>(false)
    const [isDebtFilterExpanded, setDebtFilterExpanded] = useState<boolean>(false)
    const [isHybridFilterExpanded, setHybridFilterExpanded] = useState<boolean>(false)
    const [isCommoditiesFilterExpanded, setCommoditiesFilterExpanded] = useState<boolean>(false)
    const [amcSearchText, setAmcSearchText] = useState<string>("")

    const toggleCategorySubFilters = (currentFilter: string) => {
        switch (currentFilter) {
            case "Equity":
                setEquityFilterExpanded(prevState => !prevState)
                break;
            case "Debt":
                setDebtFilterExpanded(prevState => !prevState)
                break;
            case "Hybrid":
                setHybridFilterExpanded(prevState => !prevState)
                break;
            case "Commodities":
                setCommoditiesFilterExpanded(prevState => !prevState)
                break;
        }
    }

    const categorySubFiltersStatus = (currentFilter: string) => {
        switch (currentFilter) {
            case "Equity":
                return isEquityFilterExpanded
            case "Debt":
                return isDebtFilterExpanded
            case "Hybrid":
                return isHybridFilterExpanded
            case "Commodities":
                return isCommoditiesFilterExpanded
        }
    }

    const handleClearAmcSearch = () => {
        setAmcSearchText("")
    }

    const selectedAmcFilters = amcFilterData.filter(item => item.checked)
    const availableAmcFilters = amcFilterData.filter(item => !item.checked)
    const { pathname } = useLocation();

    const orderFilter = pathname.includes("order-dashboard");

    return (
        <Box>
            <Box
                sx={(theme: Theme) => ({
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingBottom: '20px',
                    borderBottomWidth: '0.7px',
                    borderBottomStyle: 'solid',
                    borderBottomColor: `${theme.palette.text.secondary}B2`,
                })}>
                <Typography variant="h2" sx={(theme: Theme) => ({ color: theme.palette.text.primary })}>
                    Filters
                </Typography>
                <Box sx={{ display: "flex", columnGap: "10px" }}>
                    {showApplyButton && (
                        <Button
                            variant="contained"
                            sx={
                                (theme: Theme) => ({
                                    color: theme.palette.textDark.primary,
                                    backgroundColor: theme.palette.primary[500],
                                    ...theme.typography.body_500,
                                    padding: "5px"
                                })
                            }
                            onClick={handleApplyFilters}
                            // disabled={isLoading || !isFilterApplied}
                            disabled={isLoading}
                        >
                            Apply
                        </Button>
                    )}
                    <Button
                        variant="text"
                        sx={(theme: Theme) => ({
                            ...theme.typography.body_500,
                            color: theme.palette.primary[500],
                            padding: "5px"
                        })}
                        onClick={handleClearAll}
                        // disabled={isLoading || !isFilterApplied}
                        disabled={isLoading}
                    >
                        Clear All
                    </Button>
                    {showCloseIcon &&
                        <IconButton
                            aria-label="close"
                            onClick={handleCloseIconClick}
                            sx={{ padding: "5px" }}
                        >
                            <BlackExitIcon />
                        </IconButton>
                    }
                </Box>
            </Box>

            {showAmcFilter && (
                <Box
                    sx={(theme: Theme) => ({
                        marginTop: "5px",
                        paddingBottom: '25px',
                        borderBottomWidth: '0.7px',
                        borderBottomStyle: 'solid',
                        borderBottomColor: `${theme.palette.text.secondary}B2`,
                    })}>
                    <Accordion
                        // defaultExpanded={defaultExpandedFilter === "amc"}
                        defaultExpanded={false}
                        sx={{ boxShadow: 'none' }}
                    >
                        <AccordionSummary
                            expandIcon={<InvertedDrakDropDownIcon />}
                            aria-controls="amc-filter-content"
                            id="amc-filter-header"
                            sx={{
                                padding: '0px',
                            }}>
                            <Typography variant="h2" sx={(theme: Theme) => ({ color: theme.palette.text.primary })}>
                                AMC
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails
                            sx={{
                                padding: '0px',
                                marginTop: '10px',
                            }}>
                            {isLoading ? (
                                <>
                                    <Skeleton
                                        animation="wave"
                                        variant="rounded"
                                        height={47}
                                        width="100%"
                                        sx={{
                                            borderRadius: "57px"
                                        }}
                                    />
                                    <FilterLoader />
                                </>
                            ) : (
                                <>
                                    <TextField
                                        fullWidth
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        sx={{
                                                            display: amcSearchText.length > 0 ? 'inline-flex' : 'none',
                                                        }}
                                                        onClick={handleClearAmcSearch}
                                                    >
                                                        <DialogCloseIcon />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                            sx: (theme: Theme) => ({
                                                borderRadius: '57px',
                                                height: '47.17px',
                                                border: `1px solid ${theme.palette.text.secondary}B2`,
                                                '& fieldset': {
                                                    border: 'none',
                                                },
                                                '& input': {
                                                    paddingLeft: '10px',
                                                    ...theme.typography.body_500,
                                                    color: theme.palette.text.primary,
                                                    '::placeholder': {
                                                        ...theme.typography.subtitle_400,
                                                        color: theme.palette.text.secondary,
                                                        opacity: 1,
                                                    },
                                                },
                                            }),
                                        }}
                                        placeholder="Search Mutual Fund Companies"
                                        value={amcSearchText}
                                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                            setAmcSearchText(event.target.value);
                                        }}
                                    />
                                    <Box
                                        sx={(theme: Theme) => ({
                                            maxHeight: '215px',
                                            overflow: 'auto',
                                            marginTop: '25px',
                                            paddingBottom: '25px',
                                            display: `${selectedAmcFilters.length ? "block" : "none"}`,
                                            borderBottomWidth: "0.7px",
                                            borderBottomStyle: 'solid',
                                            borderBottomColor: `${theme.palette.text.secondary}B2`,
                                        })}>
                                        {selectedAmcFilters.map((filterObj: AmcFilter) => (
                                            <FilterCheckbox
                                                key={filterObj.companyId}
                                                value={filterObj.companyId}
                                                label={filterObj.companyName}
                                                checked={!!filterObj?.checked}
                                                handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                    handleAmcFilterChange(filterObj.companyId, event.target.checked)
                                                }}
                                            />
                                        ))}
                                    </Box>
                                    <Box
                                        sx={{
                                            maxHeight: '215px',
                                            overflow: 'auto',
                                            marginTop: '25px',
                                        }}>
                                        {
                                            availableAmcFilters
                                                .filter((obj: AmcFilter) => obj.companyName.toLowerCase()
                                                    .includes(amcSearchText.toLowerCase()))
                                                .map((filterObj: AmcFilter) => (
                                                    <FilterCheckbox
                                                        key={filterObj.companyId}
                                                        value={filterObj.companyId}
                                                        label={filterObj.companyName}
                                                        checked={!!filterObj?.checked}
                                                        handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                            handleAmcFilterChange(filterObj.companyId, event.target.checked)
                                                        }}
                                                    />
                                                ))}
                                    </Box>
                                </>
                            )}
                        </AccordionDetails>
                    </Accordion>
                </Box>
            )}

            {showFoliosFilter && (
                <Box
                    sx={(theme: Theme) => ({
                        marginTop: "5px",
                        paddingBottom: '25px',
                        borderBottomWidth: '0.7px',
                        borderBottomStyle: 'solid',
                        borderBottomColor: `${theme.palette.text.secondary}B2`,
                    })}>
                    <Accordion
                        defaultExpanded={defaultExpandedFilter === "folios"}
                        sx={{ boxShadow: 'none' }}
                    >
                        <AccordionSummary
                            expandIcon={<InvertedDrakDropDownIcon />}
                            aria-controls="folios-filter-content"
                            id="folios-filter-header"
                            sx={{ padding: '0px' }}>
                            <Typography
                                variant="h2"
                                sx={(theme: Theme) => ({ color: theme.palette.text.primary })}
                            >
                                Folios
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails
                            sx={{ padding: '0px', }}>
                            {isLoading ? (
                                <FilterLoader />
                            ) : (
                                foliosFilterData.map((obj: FoliosFilter) => (
                                    <FilterCheckbox
                                        key={obj.folioId}
                                        value={obj.folioId}
                                        label={obj.folioValue}
                                        checked={!!obj?.checked}
                                        handleChange={(event: React.ChangeEvent<HTMLInputElement>) => handleFoliosFilterChange(obj.folioId, event.target.checked)}
                                    />
                                )))
                            }
                        </AccordionDetails>
                    </Accordion>
                </Box>
            )}

            {showStatusFilter && (
                <Box
                    sx={(theme: Theme) => ({
                        marginTop: "5px",
                        paddingBottom: '25px',
                        borderBottomWidth: '0.7px',
                        borderBottomStyle: 'solid',
                        borderBottomColor: `${theme.palette.text.secondary}B2`,
                    })}>
                    <Accordion
                        defaultExpanded={defaultExpandedFilter === "status"}
                        sx={{ boxShadow: 'none' }}
                    >
                        <AccordionSummary
                            expandIcon={<InvertedDrakDropDownIcon />}
                            aria-controls="status-filter-content"
                            id="status-filter-header"
                            sx={{
                                padding: '0px',
                            }}>
                            <Typography
                                variant="h2"
                                sx={(theme: Theme) => ({ color: theme.palette.text.primary })}>
                                Status
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails
                            sx={{
                                padding: '0px',
                            }}>
                            {isLoading ? (
                                <FilterLoader />
                            ) : (
                                statusFilterData.map((obj: StatusFilter) => (
                                    <FilterCheckbox
                                        key={obj.statusId}
                                        value={obj.statusId}
                                        label={obj.statusValue}
                                        checked={!!obj?.checked}
                                        handleChange={(event: React.ChangeEvent<HTMLInputElement>) => handleStatusFilterChange(obj.statusId, event.target.checked)}
                                    />
                                )))
                            }
                        </AccordionDetails>
                    </Accordion>
                </Box>
            )}

            {showCategoryFilter && (
                <Box
                    sx={(theme: Theme) => ({
                        marginTop: "5px",
                        paddingBottom: '25px',
                        borderBottomWidth: '0.7px',
                        borderBottomStyle: 'solid',
                        borderBottomColor: `${theme.palette.text.secondary}B2`,
                    })}>
                    <Accordion
                        defaultExpanded={defaultExpandedFilter === "category"}
                        sx={{ boxShadow: 'none' }}
                    >
                        <AccordionSummary
                            expandIcon={<InvertedDrakDropDownIcon />}
                            aria-controls="category-filter-content"
                            id="category-filter-header"
                            sx={{
                                padding: '0px',
                            }}>
                            <Typography variant="h2" sx={(theme: Theme) => ({ color: theme.palette.text.primary })}>
                                Category
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails
                            sx={{
                                padding: '0px',
                            }}>
                            {isLoading ? (
                                <FilterLoader />
                            ) : (
                                Object.keys(categoryFilterData).map((currentCategory: string) => {
                                    const subCategoriesCount = categoryFilterData[currentCategory as keyof typeof categoryFilterData].length ? categoryFilterData[currentCategory as keyof typeof categoryFilterData].length - 1 : 0

                                    return (
                                        <Accordion
                                            key={currentCategory}
                                            sx={{
                                                boxShadow: 'none',
                                                '&:before': {
                                                    display: 'none',
                                                }
                                            }}
                                            onChange={() => toggleCategorySubFilters(currentCategory)}
                                        >
                                            <AccordionSummary
                                                expandIcon={
                                                    categorySubFiltersStatus(currentCategory) ?
                                                        <AccordianMinusIcon /> : <AccordianPlusIcon />}
                                                aria-controls={`${currentCategory}-filter`}
                                                id={`${currentCategory}-filter`}
                                                sx={{
                                                    padding: '0px',
                                                    "& .MuiAccordionSummary-content": {
                                                        margin: "0px"
                                                    }
                                                }}>
                                                <Typography
                                                    variant="body_500"
                                                    sx={(theme: Theme) => ({
                                                        color: theme.palette.text.primary,
                                                    })}
                                                >
                                                    <FilterCheckbox
                                                        value={currentCategory}
                                                        label={`${currentCategory} (${subCategoriesCount})`}
                                                        checked={!!categoryFilterData[currentCategory][0]?.checked}
                                                        handleChange={(event: React.ChangeEvent<HTMLInputElement>) => handleCategoryFilterChange(currentCategory, categoryFilterData[currentCategory][0].subCategoryId, event.target.checked)}
                                                    />
                                                </Typography>
                                            </AccordionSummary>
                                            <AccordionDetails
                                                sx={{
                                                    padding: '0px 0px 0px 20px',
                                                }}>
                                                <Box
                                                    sx={{
                                                        maxHeight: '148px',
                                                        overflow: 'auto',
                                                    }}>
                                                    {categoryFilterData[currentCategory as keyof typeof categoryFilterData]?.map((obj: CategoryFilter) => (
                                                        obj.subCategoryId !== `${currentCategory.toLowerCase()}_all` && (
                                                            <FilterCheckbox
                                                                key={obj.subCategoryId}
                                                                value={obj.subCategoryId}
                                                                label={obj.subCategoryName}
                                                                checked={!!obj?.checked}
                                                                handleChange={(event: React.ChangeEvent<HTMLInputElement>) => handleCategoryFilterChange(currentCategory, obj.subCategoryId, event.target.checked)}
                                                            />
                                                        )))}
                                                </Box>
                                            </AccordionDetails>
                                        </Accordion >
                                    )
                                })
                            )}
                        </AccordionDetails>
                    </Accordion>
                </Box>
            )}

            {showRiskFilter && (
                <Box
                    sx={(theme: Theme) => ({
                        marginTop: "5px",
                        paddingBottom: '25px',
                        borderBottomWidth: '0.7px',
                        borderBottomStyle: 'solid',
                        borderBottomColor: `${theme.palette.text.secondary}B2`,
                    })}>
                    <Accordion
                        defaultExpanded={defaultExpandedFilter === "risk"}
                        sx={{ boxShadow: 'none' }}
                    >
                        <AccordionSummary
                            expandIcon={<InvertedDrakDropDownIcon />}
                            aria-controls="risk-filter-content"
                            id="risk-filter-header"
                            sx={{
                                padding: '0px',
                            }}>
                            <Typography variant="h2" sx={(theme: Theme) => ({ color: theme.palette.text.primary })}>
                                Risk
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails
                            sx={{
                                padding: '0px',
                            }}>
                            {isLoading ? (
                                <FilterLoader />
                            ) : (
                                riskFilterData.map((obj: RiskFilter) => (
                                    <FilterCheckbox
                                        key={obj.risk_id}
                                        value={obj.risk_id}
                                        label={obj.risk_type}
                                        checked={!!obj?.checked}
                                        handleChange={(event: React.ChangeEvent<HTMLInputElement>) => handleRiskFilterChange(obj.risk_id, event.target.checked)}
                                    />
                                )))
                            }
                        </AccordionDetails>
                    </Accordion>
                </Box>
            )}
            {/* added for narc */}
            {showRatingFilter && pathname.includes('explore') && (
                <Box
                    sx={(theme: Theme) => ({
                        marginTop: "5px",
                        paddingBottom: '25px',
                        borderBottomWidth: '0.7px',
                        borderBottomStyle: 'solid',
                        borderBottomColor: `${theme.palette.text.secondary}B2`,
                    })}>
                    <Accordion
                        defaultExpanded={defaultExpandedFilter === "risk"}
                        sx={{ boxShadow: 'none' }}
                    >
                        <AccordionSummary
                            expandIcon={<InvertedDrakDropDownIcon />}
                            aria-controls="rating-filter-content"
                            id="rating-filter-header"
                            sx={{
                                padding: '0px',
                            }}>
                            <Typography variant="h2" sx={(theme: Theme) => ({ color: theme.palette.text.primary })}>
                                Rating
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails
                            sx={{
                                padding: '0px',
                            }}>
                            {isLoading ? (
                                <FilterLoader />
                            ) : (
                                ratingFilterData.map((obj: RatingFilter) => (
                                    <FilterCheckbox
                                        key={obj.rating_id}
                                        value={obj.rating_id}
                                        label={obj.rating_type}
                                        checked={!!obj?.checked}
                                        handleChange={(event: React.ChangeEvent<HTMLInputElement>) => handleRatingFilterChange(obj.rating_id, event.target.checked)}
                                    />
                                )))
                            }
                        </AccordionDetails>
                    </Accordion>
                </Box>
            )}

            {showAccountTypeFilter && (
                <Box
                    sx={(theme: Theme) => ({
                        marginTop: "5px",
                        paddingBottom: '25px',
                        borderBottomWidth: '0.7px',
                        borderBottomStyle: 'solid',
                        borderBottomColor: `${theme.palette.text.secondary}B2`,
                    })}>
                    <Accordion
                        defaultExpanded={defaultExpandedFilter === "accountType"}
                        sx={{ boxShadow: 'none' }}
                    >
                        <AccordionSummary
                            expandIcon={<InvertedDrakDropDownIcon />}
                            aria-controls="account-type-filter-content"
                            id="account-type-filter-header"
                            sx={{
                                padding: '0px',
                            }}>
                            <Typography variant="h2" sx={(theme: Theme) => ({ color: theme.palette.text.primary })}>
                                Account Type
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails
                            sx={{
                                padding: '0px',
                            }}>
                            {isLoading ? (
                                <FilterLoader />
                            ) : (
                                accountTypeFilterData.map((obj: any) => (
                                    <Box
                                        key={obj.id}
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center"
                                        }}
                                    >
                                        <FilterCheckbox
                                            value={obj.value}
                                            label={obj.label}
                                            checked={obj.checked}
                                            handleChange={(event: React.ChangeEvent<HTMLInputElement>) => handleAccountTypeFilterChange(obj.value, event.target.checked)}
                                        />
                                        {obj.isBSEActive && (
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    columnGap: "6px"
                                                }}
                                            >
                                                <BlueTickIcon />
                                                <Typography
                                                    variant='body_500'
                                                    sx={(theme: Theme) => ({
                                                        color: theme.palette.primary[500]
                                                    })}
                                                >
                                                    BSE Active
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                )))
                            }
                        </AccordionDetails>
                    </Accordion>
                </Box>
            )}

            {showInvestmentDataFilter && (
                <Box
                    sx={(theme: Theme) => ({
                        marginTop: "5px",
                        paddingBottom: '25px',
                        borderBottomWidth: '0.7px',
                        borderBottomStyle: 'solid',
                        borderBottomColor: `${theme.palette.text.secondary}B2`,
                    })}>
                    <Accordion
                        defaultExpanded={defaultExpandedFilter === "investmentData"}
                        sx={{ boxShadow: 'none' }}
                    >
                        <AccordionSummary
                            expandIcon={<InvertedDrakDropDownIcon />}
                            aria-controls="investment-data-filter-content"
                            id="investment-data-filter-header"
                            sx={{ padding: '0px' }}>
                            <Typography
                                variant="h2"
                                sx={(theme: Theme) => ({ color: theme.palette.text.primary })}
                            >
                                Investment Data
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails
                            sx={{ padding: '0px' }}>
                            {isLoading ? (
                                <FilterLoader />
                            ) : (
                                investmentFilterData.map((obj: TableHeaderData) => (
                                    <FilterCheckbox
                                        key={obj.id}
                                        value={obj.id}
                                        label={obj.label}
                                        checked={!!obj?.checked}
                                        handleChange={(event: React.ChangeEvent<HTMLInputElement>) => handleInvestmentDataFilterChange(obj.id, event.target.checked)}
                                    />
                                )))
                            }
                        </AccordionDetails>
                    </Accordion>
                </Box>
            )}

            {showMarketDataFilter && (
                <Box
                    sx={(theme: Theme) => ({
                        marginTop: "5px",
                        paddingBottom: '25px',
                        borderBottomWidth: '0.7px',
                        borderBottomStyle: 'solid',
                        borderBottomColor: `${theme.palette.text.secondary}B2`,
                    })}>
                    <Accordion
                        defaultExpanded={defaultExpandedFilter === "marketData"}
                        sx={{ boxShadow: 'none' }}
                    >
                        <AccordionSummary
                            expandIcon={<InvertedDrakDropDownIcon />}
                            aria-controls="market-data-filter-content"
                            id="market-data-filter-header"
                            sx={{ padding: '0px' }}>
                            <Typography
                                variant="h2"
                                sx={(theme: Theme) => ({ color: theme.palette.text.primary })}
                            >
                                Market Data
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails
                            sx={{ padding: '0px', }}>
                            {isLoading ? (
                                <FilterLoader />
                            ) : (
                                marketFilterData.map((obj: TableHeaderData) => (
                                    <FilterCheckbox
                                        key={obj.id}
                                        value={obj.id}
                                        label={obj.label}
                                        checked={!!obj?.checked}
                                        handleChange={(event: React.ChangeEvent<HTMLInputElement>) => handleMarketDataFilterChange(obj.id, event.target.checked)}
                                    />
                                )))
                            }
                        </AccordionDetails>
                    </Accordion>
                </Box>
            )}

            {showMarketRatiosFilter && (
                <Box
                    sx={(theme: Theme) => ({
                        marginTop: "5px",
                        paddingBottom: '25px',
                        borderBottomWidth: '0.7px',
                        borderBottomStyle: 'solid',
                        borderBottomColor: `${theme.palette.text.secondary}B2`,
                    })}>
                    <Accordion
                        defaultExpanded={defaultExpandedFilter === "marketRatios"}
                        sx={{ boxShadow: 'none' }}
                    >
                        <AccordionSummary
                            expandIcon={<InvertedDrakDropDownIcon />}
                            aria-controls="market-ratios-filter-content"
                            id="market-ratios-filter-header"
                            sx={{ padding: '0px' }}>
                            <Typography
                                variant="h2"
                                sx={(theme: Theme) => ({ color: theme.palette.text.primary })}
                            >
                                Market Ratios
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails
                            sx={{ padding: '0px', }}>
                            {isLoading ? (
                                <FilterLoader />
                            ) : (
                                marketRatiosFilterData.map((obj: TableHeaderData) => (
                                    <FilterCheckbox
                                        key={obj.id}
                                        value={obj.id}
                                        label={obj.label}
                                        checked={!!obj?.checked}
                                        handleChange={(event: React.ChangeEvent<HTMLInputElement>) => handleMarketRatiosFilterChange(obj.id, event.target.checked)}
                                    />
                                )))
                            }
                        </AccordionDetails>
                    </Accordion>
                </Box>
            )}

            {showProductTypeFilter && (
                <Box
                    sx={(theme: Theme) => ({
                        marginTop: "5px",
                        paddingBottom: '25px',
                        borderBottomWidth: '0.7px',
                        borderBottomStyle: 'solid',
                        borderBottomColor: `${theme.palette.text.secondary}B2`,
                    })}>
                    <Accordion
                        defaultExpanded={defaultExpandedFilter === "productType"}
                        sx={{ boxShadow: 'none' }}
                    >
                        <AccordionSummary
                            expandIcon={<InvertedDrakDropDownIcon />}
                            aria-controls="product-filter-content"
                            id="product-filter-header"
                            sx={{
                                padding: '0px',
                            }}>
                            <Typography variant="h2" sx={(theme: Theme) => ({ color: theme.palette.text.primary })}>
                                Product Type
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails
                            sx={{
                                padding: '0px',
                            }}>
                            {isLoading ? (
                                <FilterLoader />
                            ) : (
                                productTypeFilterData.map((obj: ProductTypeFilter) => (
                                    <FilterCheckbox
                                        key={obj.orderStatusId}
                                        value={obj.orderStatusValue}
                                        label={obj.orderStatusValue}
                                        checked={!!obj?.checked}
                                        handleChange={(event: React.ChangeEvent<HTMLInputElement>) => handleProductTypeFilterChange(obj.orderStatusId, event.target.checked)}
                                    />
                                )))
                            }
                        </AccordionDetails>
                    </Accordion>
                </Box>
            )}

            {showTransactionTypeFilter && (
                <Box
                    sx={(theme: Theme) => ({
                        marginTop: "5px",
                        paddingBottom: '25px',
                        borderBottomWidth: '0.7px',
                        borderBottomStyle: 'solid',
                        borderBottomColor: `${theme.palette.text.secondary}B2`,
                    })}>
                    <Accordion
                        defaultExpanded={defaultExpandedFilter === "transactionType"}
                        sx={{ boxShadow: 'none' }}
                    >
                        <AccordionSummary
                            expandIcon={<InvertedDrakDropDownIcon />}
                            aria-controls="transaction-filter-content"
                            id="transaction-filter-header"
                            sx={{
                                padding: '0px',
                            }}>
                            <Typography variant="h2" sx={(theme: Theme) => ({ color: theme.palette.text.primary })}>
                                Transaction Type
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails
                            sx={{
                                padding: '0px',
                            }}>
                            {isLoading ? (
                                <FilterLoader />
                            ) : (
                                transactionFilterData.map((obj: TransactionTypeFilter) => (
                                    <FilterCheckbox
                                        key={obj.orderStatusId}
                                        value={obj.orderStatusValue}
                                        label={obj.orderStatusValue}
                                        checked={!!obj?.checked}
                                        handleChange={(event: React.ChangeEvent<HTMLInputElement>) => handleTransactionTypeFilterChange(obj.orderStatusId, event.target.checked)}
                                    />
                                )))
                            }
                        </AccordionDetails>
                    </Accordion>
                </Box>
            )}

            {showPaymentTypeFilter && (
                <Box
                    sx={(theme: Theme) => ({
                        marginTop: "5px",
                        paddingBottom: '25px',
                        borderBottomWidth: '0.7px',
                        borderBottomStyle: 'solid',
                        borderBottomColor: `${theme.palette.text.secondary}B2`,
                    })}>
                    <Accordion
                        defaultExpanded={defaultExpandedFilter === "paymentType"}
                        sx={{ boxShadow: 'none' }}
                    >
                        <AccordionSummary
                            expandIcon={<InvertedDrakDropDownIcon />}
                            aria-controls="payment-filter-content"
                            id="payment-filter-header"
                            sx={{
                                padding: '0px',
                            }}>
                            <Typography variant="h2" sx={(theme: Theme) => ({ color: theme.palette.text.primary })}>
                                {`Payment Status`}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails
                            sx={{
                                padding: '0px',
                            }}>
                            {isLoading ? (
                                <FilterLoader />
                            ) : (
                                paymentFilterData.map((obj: PaymentTypeFilter) => (
                                    <FilterCheckbox
                                        key={obj.orderStatusId}
                                        value={obj.orderStatusValue}
                                        label={obj.orderStatusValue}
                                        checked={!!obj?.checked}
                                        handleChange={(event: React.ChangeEvent<HTMLInputElement>) => handlePaymentTypeFilterChange(obj.orderStatusId, event.target.checked)}
                                    />
                                )))
                            }
                        </AccordionDetails>
                    </Accordion>
                </Box>
            )}

            {showApprovalTypeFilter && (
                <Box
                    sx={(theme: Theme) => ({
                        marginTop: "5px",
                        paddingBottom: '25px',
                        borderBottomWidth: '0.7px',
                        borderBottomStyle: 'solid',
                        borderBottomColor: `${theme.palette.text.secondary}B2`,
                    })}>
                    <Accordion
                        defaultExpanded={defaultExpandedFilter === (orderFilter ? "orderType" : "productType")}
                        sx={{ boxShadow: 'none' }}
                    >
                        <AccordionSummary
                            expandIcon={<InvertedDrakDropDownIcon />}
                            aria-controls="approval-filter-content"
                            id="approval-filter-header"
                            sx={{
                                padding: '0px',
                            }}>
                            <Typography variant="h2" sx={(theme: Theme) => ({ color: theme.palette.text.primary })}>
                                {orderFilter ? `Order Status` : `Approval Type`}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails
                            sx={{
                                padding: '0px',
                            }}>
                            {isLoading ? (
                                <FilterLoader />
                            ) : (
                                approvalTypeFilterData.map((obj: PaymentTypeFilter) => (
                                    <FilterCheckbox
                                        key={obj.orderStatusId}
                                        value={obj.orderStatusValue}
                                        label={obj.orderStatusValue}
                                        checked={!!obj?.checked}
                                        handleChange={(event: React.ChangeEvent<HTMLInputElement>) => handleApprovalTypeFilterChange(obj.orderStatusId, event.target.checked)}
                                    />
                                )))
                            }
                        </AccordionDetails>
                    </Accordion>
                </Box>
            )}
        </Box >
    );
}
